import { useTranslation } from 'react-i18next';

import { Button, Card, CardContent } from '@saas-kit/ui';

import type { AdminTenant } from './api';
import { TenantResetPasswordAction } from './TenantResetPasswordAction';
import { TenantStatusBadge } from './TenantStatusBadge';

type ConfirmKind = 'suspend' | 'reactivate' | 'archive';

interface Props {
  tenant: AdminTenant;
  isBusy: boolean;
  onConfirm: (kind: ConfirmKind) => void;
}

export function TenantDetailOverview({
  tenant,
  isBusy,
  onConfirm,
}: Props): JSX.Element {
  const { t } = useTranslation('adminTenants');
  return (
    <Card>
      <CardContent className="space-y-4 py-6">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('detail.overview.id')}
            </dt>
            <dd className="break-all font-mono text-xs">{tenant.id}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('detail.overview.slug')}
            </dt>
            <dd className="font-mono text-xs">{tenant.slug}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('detail.overview.status')}
            </dt>
            <dd>
              <TenantStatusBadge status={tenant.status} />
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('detail.overview.created_at')}
            </dt>
            <dd>{new Date(tenant.created_at).toLocaleString()}</dd>
          </div>
        </dl>

        <div className="flex flex-wrap gap-2">
          <TenantResetPasswordAction tenantId={tenant.id} disabled={isBusy} />
          {tenant.status === 'active' ? (
            <Button
              variant="secondary"
              disabled={isBusy}
              onClick={() => onConfirm('suspend')}
            >
              {t('actions.suspend')}
            </Button>
          ) : null}
          {tenant.status === 'suspended' ? (
            <Button
              variant="secondary"
              disabled={isBusy}
              onClick={() => onConfirm('reactivate')}
            >
              {t('actions.reactivate')}
            </Button>
          ) : null}
          {tenant.status !== 'archived' ? (
            <Button
              variant="destructive"
              disabled={isBusy}
              onClick={() => onConfirm('archive')}
            >
              {t('actions.archive')}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
